import React from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { hexToRGB, toRenderableHtml } from 'utils';
import styled from 'styled-components';
import { useNavigate, useOutletContext, useLocation, useSearchParams } from 'react-router-dom';
import { Button, Icon, SubTitle, Loading } from 'components';
import { ColDiv, Container, RowDiv, Text, ContentBody } from 'styles';
import useDownload from 'hooks/useDownload';

import { BOARD_NOTICE_MODULE_NO } from 'data/constants';

import boardService from 'services/board/board-service';

// 기본은 공지사항(module 1). 다른 게시판은 moduleNo/title/desc/basePath 를 넘겨 재사용 (pages/community/board/BoardDetail.js)
export function NoticeDetail({
  moduleNo = BOARD_NOTICE_MODULE_NO,
  title,
  desc,
  basePath = '/community/notice',
  useQueryContentNo = false,
}) {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // 목록에서 넘어온 state 우선, 새로고침/직접 진입 시 ?contentNo= 사용
  const contentNo = (location.state && location.state.contentNo) || searchParams.get('contentNo');

  const navigate = useNavigate();
  const { langList } = useOutletContext();
  const {t} = useTranslation();
  const {downloadBySiteFileId} = useDownload();

  const [notice, setNotice] = React.useState({});
  const [loading, setLoading] = React.useState(true);

  const subject = title !== undefined ? title : langList.notice.subject;
  const subjectDesc = desc !== undefined ? desc : langList.notice.subjectDesc;

  const goContent = (no) => {
    navigate(`${basePath}/view?contentNo=${no}`, { state: { contentNo: no } });
  };

  React.useEffect(() => {
    async function fetchNotice() {
      let data = {};

      try {
        const response = await boardService.getContent(moduleNo, contentNo);

        if (response) {
          data = response;
        }
      } catch(e) {
        console.error(e);
      }

      setNotice(data.content || {});
      // 글 제목을 문서 제목에 반영 (서버 렌더링 title 과 동일 형식: 글제목 | 게시판명 | 팜인)
      if (data.content && data.content.title) {
        document.title = `${data.content.title} | ${subject} | 팜인`;
      }
      setLoading(false);
    }

    fetchNotice()
    return () => setLoading(true);
  }, [contentNo, moduleNo]);

  return (
    <>
      {loading && <Loading />}
      <Container $gap={48} $width="100%">
        <SubTitle title={subject} desc={subjectDesc} />
        <ContentBox>
          <TitleBox $padding="48px" $mPadding="24px" $gap={16} $mGap={8}>
            <Text $type="h4" $mType="h5">
              {notice.title}
            </Text>
            <RowDiv $gap={8} $alignItems="center">
              <Icon name="clockPrimary" $width={14} $height={14} />
              <Text $type="b7" $color={hexToRGB('#171717', 0.4)} $fontWeight={500}>
                {notice.regDate ? dayjs(notice.regDate).format(t('date_format')) : ''}
              </Text>
            </RowDiv>
          </TitleBox>
          <ColDiv $padding="48px" $mPadding="24px" $gap={48} $mGap={24}>
            <ContentBody dangerouslySetInnerHTML={{ __html: toRenderableHtml(notice.content) }} />
            {notice.attachFiles && notice.attachFiles.length > 0 && (
              <FileBox $gap={8}>
                {notice.attachFiles.map((file, index) => (
                  <RowDiv key={`file_${index}`} onClick={() => {
                    downloadBySiteFileId(file.siteFileId, file.originalFileName);
                  }}>
                    <RowDiv $alignItems="center">
                      <Icon name="file" $width={15} $height={16} $padding="4px 4px 4px 5px" />
                      <Text $type="b7" $mType="b8" $color={hexToRGB('#171717', 0.4)} $fontWeight={500}>
                        {langList.notice.attachedFile}
                      </Text>
                    </RowDiv>
                    <RowDiv $alignItems="center">
                      <Text $type="b7" $mType="b8" $color={hexToRGB('#171717', 0.87)} $fontWeight={500}>
                        {file.originalFileName}
                      </Text>
                    </RowDiv>
                  </RowDiv>
                ))}
              </FileBox>
            )}
          </ColDiv>
        </ContentBox>
        <BtnBox $justifyContent="space-between">
          { notice.previousContent ?
            <RowDiv $hover $alignItems="center" $gap={16} $mGap={4} onClick={() => {
              goContent(notice.previousContent.contentNo);
            }}>
              <Icon name="arrow" $transform="rotate(90deg)" />
              <Text $type="b7" $color="darker" $maxLine={1}>
                {langList.notice.prev}
              </Text>
            </RowDiv>
            :
            <RowDiv $hover $alignItems="center" $gap={16} $mGap={4}></RowDiv>
          }
          <Button label="목록" btnStyle="darkLine" tBtnType="medium" onClick={() => navigate(-1)} />
          { notice.nextContent ?
            <RowDiv $hover $alignItems="center" $gap={16} $mGap={4} onClick={() => {
              goContent(notice.nextContent.contentNo);
            }}>
              <Text $type="b7" $color="darker" $maxLine={1}>
                {langList.notice.next}
              </Text>
              <Icon name="arrow" $transform="rotate(-90deg)" />
            </RowDiv>
            :
            <RowDiv $hover $alignItems="center" $gap={16} $mGap={4}></RowDiv>
          }
        </BtnBox>
      </Container>
    </>

  );
}

const TitleBox = styled(ColDiv)`
  border-bottom: 1px solid ${hexToRGB('#171717', 0.12)};
`;

const ContentBox = styled.div`
  border-top: 2px solid ${({ theme }) => theme.colors.primary900};
  border-bottom: 2px solid ${({ theme }) => theme.colors.primary900};
`;

const FileBox = styled(ColDiv)`
  & div {
    background-color: ${({ theme }) => theme.colors.lightest};
    cursor: pointer;
  }
  & div > div {
    padding: 16px 24px;
  }

  @media ${({ theme }) => theme.device.mobile} {
    & div > div {
      padding: 16px 4px;
    }
  }
`;

const BtnBox = styled(RowDiv)`
  @media ${({ theme }) => theme.device.mobile} {
    position: relative;

    & div {
      max-width: 82px;
    }
    & > button {
      position: absolute;
      left: 50%;
      transform: translateX(-50%);
      bottom: -62px;
    }
  }
`;
